"use client"

import { addNewLead } from "@/actions/lead"
import { Boilerplate } from "@prisma/client"
import { useFormik } from "formik"
import { useCallback, useState } from "react"
import { z } from "zod"
import { toFormikValidationSchema } from "zod-formik-adapter"

import { toast } from "@/hooks/use-toast"

import { Button } from "./ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog"
import { Input } from "./ui/input"

const schema = z.object({
  email: z.string().email(),
  xHandler: z.string().optional(),
})

export default function ReductionPopupModal({
  isOpen,
  setIsOpen,
  boilerplate,
}: {
  isOpen: boolean
  setIsOpen: (isOpen: boolean) => void
  boilerplate: Boilerplate
}) {
  const formik = useFormik({
    initialValues: {
      email: "",
      xHandler: "",
    },
    validationSchema: toFormikValidationSchema(schema),
    onSubmit: (params) => {
      addNewLead({
        email: params.email,
        xHandler: params.xHandler,
        boilerplateId: boilerplate.id,
      })
        .then(() => {
          setIsOpen(false)
          toast({
            title: "Success",
            description:
              "We will notify you when the price drops or a promotion is available! 🎉",
          })
        })
        .catch(() => {
          toast({
            title: "Error",
            description: "Try again.",
          })
        })
    },
  })
  return (
    <Dialog
      open={isOpen}
      onOpenChange={(val) => {
        setIsOpen(val)
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Not ready to purchase {boilerplate.title} yet?
          </DialogTitle>
          <DialogDescription>
            Enter your email to receive a notification when the price drops or
            to get a promotion code for this product. 🎁
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2">
          <Input placeholder="joe@gmail.com" label="Email*" {...formik.getFieldProps("email")} />
          <Input label="X handler" placeholder="@YannisHaismann" {...formik.getFieldProps("xHandler")} />
        </div>
        <DialogFooter>
          <Button
            variant={"secondary"}
            onClick={() => {
              setIsOpen(false)
            }}
          >
            Close
          </Button>
          <Button
            className="min-w-40"
            onClick={() => {
              formik.handleSubmit()
            }}
            type="submit"
          >
            Submit
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function useReductionPopupModal(boilerplate: Boilerplate) {
  const [isOpen, setIsOpen] = useState(false)

  function open() {
    setIsOpen(true)
  }

  function close() {
    setIsOpen(false)
  }

  const modal = useCallback(() => {
    return (
      <ReductionPopupModal
        setIsOpen={setIsOpen}
        isOpen={isOpen}
        boilerplate={boilerplate}
      />
    )
  }, [isOpen, boilerplate, setIsOpen])

  return { isOpen, open, close, modal }
}
